/**
 * behaviorDetect — AI-based detection of FINN behavior evidence in delivery notes.
 * Replaces the old keyword-matching approach which was too strict for natural language.
 */
import { authenticatedFetch } from '../../../../shared/utils/authenticatedFetch';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-520260363689.europe-west3.run.app';

export interface BehaviorMatch {
  valueName: string;
  excerpt: string;
  sentiment: 'positive' | 'negative';
}

const VALUE_DESCRIPTIONS: Record<string, string> = {
  'Aim High': 'growth mindset, urgency, ambition, challenges status quo, high standards, entrepreneurial spirit',
  'Own It': 'full accountability, ownership, tenacity, resilience, hands-on delivery, learning from mistakes',
  'Win Together': 'cross-functional collaboration, FINN first, radical candor, stakeholder relationships, building trust',
  'Embrace Openness': 'constructive feedback, inclusion, psychological safety, transparency, welcoming diverse perspectives',
  'Obsess about Customers': 'customer value, customer needs, curiosity, subscriber experience, customer-first decisions',
};

// Words that indicate a null response when they appear at the start of the excerpt.
const NULL_STARTS = ['none', 'n/a', 'not applicable', 'no content', 'no relevant', 'nothing', '...', '–', '—'];
// Phrases that indicate a null response wherever they appear in the excerpt.
// Kept specific to avoid false positives on real insights (e.g. "showed none of the hesitation").
const NULL_CONTAINS = ['no evidence', 'no relevant', 'no examples', 'not demonstrated', 'no instances', 'no specific evidence', 'no clear evidence'];

function isNullExcerpt(lower: string): boolean {
  return NULL_STARTS.some(n => lower.startsWith(n)) || NULL_CONTAINS.some(n => lower.includes(n));
}

function parseMatches(text: string, valueName: string): BehaviorMatch[] {
  const matches: BehaviorMatch[] = [];
  text.split('\n').forEach(line => {
    const trimmed = line.trim();
    const upper = trimmed.toUpperCase();
    if (upper.startsWith('[POSITIVE]')) {
      const excerpt = trimmed.slice('[POSITIVE]'.length).trim();
      const lower = excerpt.toLowerCase();
      if (excerpt.length > 10 && !isNullExcerpt(lower)) {
        matches.push({ valueName, excerpt, sentiment: 'positive' });
      }
    } else if (upper.startsWith('[NEGATIVE]')) {
      const excerpt = trimmed.slice('[NEGATIVE]'.length).trim();
      const lower = excerpt.toLowerCase();
      if (excerpt.length > 10 && !isNullExcerpt(lower)) {
        matches.push({ valueName, excerpt, sentiment: 'negative' });
      }
    }
  });
  return matches;
}

function buildDeliveryText(
  deliverables: Array<{ title?: string; wellDone?: string; improvement?: string }>,
): string {
  return deliverables
    .filter(d => d.wellDone?.trim() || d.improvement?.trim())
    .map((d, i) => {
      const parts: string[] = [];
      if (d.title?.trim()) parts.push(`Deliverable: ${d.title.trim()}`);
      if (d.wellDone?.trim()) parts.push(`What went well: ${d.wellDone.trim()}`);
      if (d.improvement?.trim()) parts.push(`What to improve: ${d.improvement.trim()}`);
      return `[D${i + 1}]\n${parts.join('\n')}`;
    })
    .join('\n\n');
}

export async function detectBehaviorsForValue(
  deliverables: Array<{ title?: string; wellDone?: string; improvement?: string }>,
  valueName: string,
): Promise<BehaviorMatch[]> {
  const deliveryText = buildDeliveryText(deliverables);
  if (!deliveryText) return [];

  const valueDesc = VALUE_DESCRIPTIONS[valueName] || valueName;

  const prompt = `You are helping a manager identify evidence of the FINN value "${valueName}" in their delivery notes.

"${valueName}" means: ${valueDesc}

DELIVERY NOTES:
${deliveryText}

Find relevant evidence for "${valueName}" only:
- [POSITIVE] = the employee demonstrated this value
- [NEGATIVE] = there was a gap in this value

Rules:
- Quote directly from the manager's text where possible. Do not paraphrase or invent.
- If no evidence exists, produce no output at all — no explanation, no placeholder, no comment.
- Max 2 excerpts total, 1–2 sentences each.

Output format:
[POSITIVE] ...
[NEGATIVE] ...`;

  const res = await authenticatedFetch(
    `${PROXY_URL}/api/people/manager-review-v2/generate`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompt }),
    },
  );
  if (!res.ok) throw new Error(`Detection failed: ${res.status}`);
  const json = await res.json() as { text?: string };
  return parseMatches(json.text || '', valueName);
}

